
import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit2, Trash2, Clock, User, CheckCircle2, Circle, PlayCircle } from "lucide-react";
import { ProjectSubtask } from '../types';

interface SubtasksListProps {
  subtasks: ProjectSubtask[];
  selectedTaskId: string | null;
  isLoading: boolean;
  onEdit: (subtask: ProjectSubtask) => void;
  onDelete: (subtaskId: string) => void;
  onStatusChange: (subtaskId: string, status: 'To Do' | 'In Progress' | 'Done') => void;
  isRestrictedUser?: boolean;
}

const SubtasksList = ({ 
  subtasks, 
  selectedTaskId, 
  isLoading, 
  onEdit, 
  onDelete, 
  onStatusChange,
  isRestrictedUser = false
}: SubtasksListProps) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Done':
        return 'bg-green-500/20 text-green-300 border-green-500/30 hover:bg-green-500/30';
      case 'In Progress':
        return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30 hover:bg-yellow-500/30';
      default:
        return 'bg-gray-500/20 text-gray-300 border-gray-500/30 hover:bg-gray-500/30';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Done':
        return <CheckCircle2 className="h-2.5 w-2.5" />;
      case 'In Progress':
        return <PlayCircle className="h-2.5 w-2.5" />;
      default:
        return <Circle className="h-2.5 w-2.5" />;
    }
  };

  const getNextStatus = (currentStatus: string) => {
    switch (currentStatus) {
      case 'To Do':
        return 'In Progress';
      case 'In Progress':
        return 'Done';
      case 'Done':
        return 'To Do';
      default:
        return 'To Do';
    }
  };

  const getCardGradient = (status: string) => {
    switch (status) {
      case 'Done':
        return 'bg-gradient-to-br from-green-500/20 via-green-400/10 to-emerald-500/20';
      case 'In Progress':
        return 'bg-gradient-to-br from-yellow-500/20 via-orange-400/10 to-amber-500/20';
      default:
        return 'bg-gradient-to-br from-gray-500/20 via-slate-400/10 to-gray-600/20';
    }
  };

  if (!selectedTaskId) {
    return (
      <div className="p-6 text-center">
        <div className="text-gray-300 text-sm mb-3">
          Select a task from the progress overview to view and manage its subtasks.
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6 text-center">
        <div className="text-gray-300 text-sm">Loading subtasks...</div>
      </div>
    );
  }

  if (subtasks.length === 0) {
    return (
      <div className="p-6 text-center">
        <div className="text-gray-300 text-sm mb-3">
          No subtasks found for this task.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 max-h-[518px] overflow-y-auto pr-2">
      {subtasks.map((subtask) => (
        <Card 
          key={subtask.id} 
          className={`${getCardGradient(subtask.status)} border border-white hover:border-primary/40 transition-all duration-300 hover:shadow-lg hover:shadow-primary/10 backdrop-blur-sm`}
        >
          <CardContent className="p-2">
            <div className="flex items-start justify-between mb-1">
              <div className="flex items-center gap-2 flex-1">
                <Badge 
                  variant="outline" 
                  className={`cursor-pointer transition-all duration-200 flex items-center gap-1 px-1.5 py-0.5 text-xs ${getStatusColor(subtask.status)}`}
                  onClick={() => onStatusChange(subtask.id, getNextStatus(subtask.status) as 'To Do' | 'In Progress' | 'Done')}
                >
                  {getStatusIcon(subtask.status)}
                  {subtask.status}
                </Badge>
                <h3 className="text-white font-medium text-xs flex-1">{subtask.subtask_name}</h3>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEdit(subtask)}
                  disabled={isRestrictedUser}
                  className={`transition-colors h-5 w-5 p-0 ${
                    isRestrictedUser 
                      ? "text-gray-500 cursor-not-allowed hover:text-gray-500 hover:bg-transparent"
                      : "text-gray-300 hover:text-white hover:bg-primary/20"
                  }`}
                >
                  <Edit2 className="h-2.5 w-2.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(subtask.id)}
                  disabled={isRestrictedUser}
                  className={`transition-colors h-5 w-5 p-0 ${
                    isRestrictedUser 
                      ? "text-gray-500 cursor-not-allowed hover:text-gray-500 hover:bg-transparent"
                      : "text-red-300 hover:text-red-200 hover:bg-red-500/20"
                  }`}
                >
                  <Trash2 className="h-2.5 w-2.5" />
                </Button>
              </div>
            </div>

            {subtask.description && (
              <div className="mb-1 p-1.5 bg-secondary/30 rounded border border-primary/10">
                <p className="text-gray-300 text-xs leading-relaxed">{subtask.description}</p>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-1 text-xs">
              {(subtask.name || subtask.role) && (
                <div className="flex items-center gap-1 bg-primary/10 px-1.5 py-0.5 rounded-full border border-primary/20">
                  <User className="h-2.5 w-2.5 text-primary" />
                  <span className="text-gray-200 text-xs">
                    {subtask.name && subtask.role ? `${subtask.name} - ${subtask.role}` : 
                     subtask.name ? subtask.name : 
                     subtask.role ? subtask.role : 
                     'Assigned Resource'}
                  </span>
                </div>
              )}
              
              {subtask.estimated_hours && (
                <div className="flex items-center gap-1 bg-blue-500/10 px-1.5 py-0.5 rounded-full border border-blue-500/20">
                  <Clock className="h-2.5 w-2.5 text-blue-400" />
                  <span className="text-gray-200 text-xs">
                    Est: {subtask.estimated_hours}h
                  </span>
                </div>
              )}

              {subtask.actual_hours !== undefined && subtask.actual_hours !== null && (
                <div className="flex items-center gap-1 bg-green-500/10 px-1.5 py-0.5 rounded-full border border-green-500/20">
                  <Clock className="h-2.5 w-2.5 text-green-400" />
                  <span className="text-gray-200 text-xs">
                    Actual: {subtask.actual_hours}h
                  </span>
                </div>
              )}

              {subtask.start_date && subtask.end_date && (
                <div className="flex items-center gap-1 bg-purple-500/10 px-1.5 py-0.5 rounded-full border border-purple-500/20">
                  <span className="text-gray-200 text-xs">
                    {new Date(subtask.start_date).toLocaleDateString()} - {new Date(subtask.end_date).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default SubtasksList;
